import { useMemo } from 'react';
import type { GetApiV3PermitsCertificatesRequirementsRequiredParams } from '@/libs/api-rest/generated/model';
import { useGetApiV3PermitsCertificatesRequirementsRequired } from '@/libs/api-rest/generated/permits-certificates-requirements/permits-certificates-requirements';
import { mapRequirementDtoList } from '../mappers/requirementsMapper';
import type { JobRequirement } from '@/raptorjob/domain/types/requirementTypes';

interface UseJobRequirementsParams {
  /** Pour / Raptor job id when evaluating an existing job; omit or ≤0 for create flow (customer/job site only). */
  jobId?: number;
  jobSiteId?: number;
  customerId?: number;
}

interface UseJobRequirementsResult {
  requirements: JobRequirement[];
  permits: JobRequirement[];
  certifications: JobRequirement[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useJobRequirements({ jobId, jobSiteId, customerId }: UseJobRequirementsParams): UseJobRequirementsResult {
  const shouldFetch =
    (jobId != null && Number(jobId) > 0) || (customerId != null && Number(customerId) > 0) || (jobSiteId != null && Number(jobSiteId) > 0);

  const params = useMemo((): GetApiV3PermitsCertificatesRequirementsRequiredParams | undefined => {
    if (!shouldFetch) return undefined;
    const p: GetApiV3PermitsCertificatesRequirementsRequiredParams = {};
    if (customerId != null && Number(customerId) > 0) {
      p.CustomerId = customerId;
    }
    if (jobSiteId != null && Number(jobSiteId) > 0) {
      p.JobSiteId = jobSiteId;
    }
    if (jobId != null && Number(jobId) > 0) {
      p.PourId = jobId;
    }
    return p;
  }, [shouldFetch, customerId, jobId, jobSiteId]);

  const query = useGetApiV3PermitsCertificatesRequirementsRequired(params, {
    query: {
      enabled: shouldFetch,
      staleTime: 10 * 60 * 1000, // 10 minutes
      select: (response) => mapRequirementDtoList(response.data.items?.items ?? [])
    }
  });

  const requirements = query.data ?? [];
  const permits = requirements.filter((req: JobRequirement) => req.requirementType === 'permit');
  const certifications = requirements.filter((req: JobRequirement) => req.requirementType === 'certification');
  const error = query.error instanceof Error ? query.error : null;

  return {
    requirements,
    permits,
    certifications,
    isLoading: query.isLoading,
    error,
    refetch: () => {
      void query.refetch();
    }
  };
}
